import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  type ActiveModel,
  type FeatureGroupWeights,
  type ModelConfig,
  type ModelLabSplitPreview,
  type ModelRun,
  type TimeSplit,
} from "../lib/api";
import { useActiveModel } from "../lib/activeModel";
import { useAuth } from "../lib/auth";

const FEATURE_GROUPS: {
  key: string;
  label: string;
  description: string;
}[] = [
  {
    key: "market",
    label: "Market",
    description: "Closing lines, money-line implied probs, market movement.",
  },
  {
    key: "team_strength",
    label: "Team strength",
    description: "Net rating, recent form, Elo-style ratings.",
  },
  {
    key: "pace",
    label: "Pace",
    description: "Tempo and possessions-per-game style features.",
  },
  {
    key: "rest",
    label: "Rest & travel",
    description: "Days of rest, back-to-backs, travel distance.",
  },
];

function defaultWeights(): FeatureGroupWeights {
  return Object.fromEntries(FEATURE_GROUPS.map((g) => [g.key, 1.0]));
}

function todayISO(offsetDays = 0): string {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

type SplitPresetKey = "default_30" | "last_60" | "last_90" | "custom";

function splitFromPreset(preview: ModelLabSplitPreview, preset: SplitPresetKey): TimeSplit | null {
  const max = preview.labeled_game_date_range.max;
  const min = preview.labeled_game_date_range.min;
  if (!max || !min) return null;

  const evalDays = preset === "last_60" ? 60 : preset === "last_90" ? 90 : 30;
  const maxD = new Date(`${max}T00:00:00Z`);
  const minD = new Date(`${min}T00:00:00Z`);
  const evalEnd = maxD;
  const evalStart = new Date(evalEnd);
  evalStart.setUTCDate(evalStart.getUTCDate() - (evalDays - 1));
  if (evalStart < minD) evalStart.setTime(minD.getTime());

  const trainEnd = new Date(evalStart);
  trainEnd.setUTCDate(trainEnd.getUTCDate() - 1);
  if (trainEnd < minD) trainEnd.setTime(minD.getTime());

  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { train_end_date: iso(trainEnd), eval_start_date: iso(evalStart), eval_end_date: iso(evalEnd) };
}

function useLocalStorage<T>(key: string, initialValue: T | (() => T)) {
  const [value, setValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      if (item !== null) {
        return JSON.parse(item) as T;
      }
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error as Error);
    }
    return initialValue instanceof Function ? initialValue() : initialValue;
  });

  const setValueWrapped = useCallback((valueToStore: T | ((val: T) => T)) => {
    setValue((prevValue) => {
      try {
        const valueToSave =
          valueToStore instanceof Function ? valueToStore(prevValue) : valueToStore;
        if (valueToSave === undefined) {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(valueToSave));
        }
        return valueToSave;
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error as Error);
        return prevValue;
      }
    });
  }, [key]);

  return [value, setValueWrapped] as const;
}

function formatFixed4(n: unknown): string {
  return typeof n === "number" && Number.isFinite(n) ? n.toFixed(4) : "—";
}

export function ModelLabPage() {
  const { user } = useAuth();
  const { summary: activeModelSummary, error: activeModelError, refresh: refreshActiveModel } = useActiveModel();
  const storageNs = useMemo(() => {
    const email = user?.email?.trim().toLowerCase();
    return email ? `modelLab:${email}` : "modelLab:anon";
  }, [user?.email]);
  const hydratedNsRef = useRef<string | null>(null);

  const [label, setLabel] = useLocalStorage(`${storageNs}.label`, "my-first-model");
  const [weights, setWeights] = useLocalStorage<FeatureGroupWeights>(`${storageNs}.weights`, defaultWeights);
  const [preset, setPreset] = useLocalStorage<SplitPresetKey>(`${storageNs}.splitPreset`, "default_30");
  const [trainEnd, setTrainEnd] = useLocalStorage(`${storageNs}.trainEnd`, () => todayISO(-30));
  const [evalStart, setEvalStart] = useLocalStorage(`${storageNs}.evalStart`, () => todayISO(-29));
  const [evalEnd, setEvalEnd] = useLocalStorage(`${storageNs}.evalEnd`, () => todayISO(-1));
  const [config, setConfig] = useLocalStorage<ModelConfig | null>(`${storageNs}.config`, null);
  const [run, setRun] = useLocalStorage<ModelRun | null>(`${storageNs}.run`, null);
  const [configsCatalog, setConfigsCatalog] = useState<ModelConfig[]>([]);
  const [runsCatalog, setRunsCatalog] = useState<ModelRun[]>([]);
  const [activeModel, setActiveModel] = useState<ActiveModel | null>(null);
  const [activateMessage, setActivateMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState<"config" | "run" | "activate" | "hydrate" | "load_run" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [splitPreview, setSplitPreview] = useState<ModelLabSplitPreview | null>(null);
  const [splitPreviewBusy, setSplitPreviewBusy] = useState(false);

  const splitValid = useMemo(() => {
    if (preset === "custom") return trainEnd < evalStart && evalStart <= evalEnd;
    return splitPreview?.ok === true;
  }, [preset, trainEnd, evalStart, evalEnd, splitPreview]);

  const configWeightsMissing = useMemo(() => {
    if (!config) return false;
    return Object.keys(config.feature_group_weights).length === 0;
  }, [config]);

  const activeRunSummary = useMemo(() => {
    if (!activeModel?.run_id) return null;
    const r = runsCatalog.find((x) => x.run_id === activeModel.run_id);
    const cfg = configsCatalog.find((c) => c.config_id === r?.config_id);
    const cfgTitle = (cfg?.label ?? cfg?.display_name ?? r?.config_id ?? "").trim();
    return { run: r, cfgTitle };
  }, [activeModel?.run_id, runsCatalog, configsCatalog]);

  useEffect(() => {
    hydratedNsRef.current = null;
  }, [storageNs]);

  // Hydrate server-side Model Lab state for this authenticated user.
  useEffect(() => {
    if (!user) return;
    if (hydratedNsRef.current === storageNs) return;
    const idToken = user.idToken;
    const ctrl = new AbortController();
    void (async () => {
      setBusy("hydrate");
      setError(null);
      try {
        const [configs, runs] = await Promise.all([
          api.listModelConfigs({ idToken, signal: ctrl.signal, limit: 50 }),
          api.listModelRuns({ idToken, signal: ctrl.signal, limit: 50 }),
        ]);
        setConfigsCatalog(configs);
        setRunsCatalog(runs);

        const preferredConfig =
          (config?.config_id ? configs.find((c) => c.config_id === config.config_id) : null) ??
          configs[0] ??
          null;

        if (preferredConfig) {
          setConfig(preferredConfig);
          const nextLabel = preferredConfig.label ?? preferredConfig.display_name;
          if (nextLabel) setLabel(nextLabel);
          setWeights({ ...defaultWeights(), ...preferredConfig.feature_group_weights });
        } else {
          setConfig(null);
          setRun(null);
        }

        const pollable = runs.filter((r) => r.status === "pending" || r.status === "running");
        const terminal = runs.filter((r) => r.status !== "pending" && r.status !== "running");
        const preferredRun =
          (run?.run_id ? runs.find((r) => r.run_id === run.run_id) : null) ??
          pollable[0] ??
          terminal.find((r) => r.config_id === (preferredConfig?.config_id ?? "")) ??
          terminal[0] ??
          null;

        if (preferredRun) {
          // Always refresh from the canonical GET endpoint so metrics/embeddings match server.
          const latestRun = await api.getModelRun(preferredRun.run_id, {
            idToken,
            signal: ctrl.signal,
          });
          setRun(latestRun);
        } else {
          setRun(null);
        }

        const active = await api.getActiveModel({ idToken, signal: ctrl.signal });
        setActiveModel(active);

        hydratedNsRef.current = storageNs;
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setError((err as Error).message);
        }
      } finally {
        setBusy((prev) => (prev === "hydrate" ? null : prev));
      }
    })();
    return () => ctrl.abort();
  }, [user, storageNs, setConfig, setLabel, setRun, setWeights, config?.config_id, run?.run_id]);

  const pollRunId = run?.run_id;
  const pollRunStatus = run?.status;

  useEffect(() => {
    if (!user || !pollRunId || !pollRunStatus || !["pending", "running"].includes(pollRunStatus)) {
      return;
    }
    const runId = pollRunId;
    const timer = setInterval(async () => {
      try {
        const latestRun = await api.getModelRun(runId, {
          idToken: user.idToken,
        });
        setRun(latestRun);
      } catch (err) {
        console.error("Failed to poll run status", err as Error);
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [user, pollRunId, pollRunStatus, setRun]);

  // Preview split: drives the UX (counts + guardrails) and lets presets be date-free.
  useEffect(() => {
    if (!user || !config) {
      setSplitPreview(null);
      return;
    }
    const ctrl = new AbortController();
    const t = window.setTimeout(async () => {
      setSplitPreviewBusy(true);
      setError(null);
      try {
        if (preset === "custom") {
          const res = await api.previewModelLabSplit(
            {
              split: {
                train_end_date: trainEnd,
                eval_start_date: evalStart,
                eval_end_date: evalEnd,
              },
            },
            { idToken: user.idToken, signal: ctrl.signal },
          );
          setSplitPreview(res.preview);
        } else {
          // First ask server for default preview (includes labeled range), then derive preset split.
          const base = await api.previewModelLabSplit({}, { idToken: user.idToken, signal: ctrl.signal });
          const derived = splitFromPreset(base.preview, preset);
          if (!derived) {
            setSplitPreview(base.preview);
            return;
          }
          const res = await api.previewModelLabSplit({ split: derived }, { idToken: user.idToken, signal: ctrl.signal });
          setSplitPreview(res.preview);
        }
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          setSplitPreview(null);
          setError((err as Error).message);
        }
      } finally {
        setSplitPreviewBusy(false);
      }
    }, 250);
    return () => {
      ctrl.abort();
      window.clearTimeout(t);
    };
  }, [user, config, preset, trainEnd, evalStart, evalEnd]);

  const setWeight = useCallback((key: string, v: number) => {
    setWeights((prev) => ({ ...prev, [key]: v }));
  }, [setWeights]);

  const onSelectSavedConfig = useCallback(
    async (configId: string) => {
      if (!user) return;
      const selected = configsCatalog.find((c) => c.config_id === configId) ?? null;
      if (!selected) {
        setConfig(null);
        return;
      }
      setConfig(selected);
      const nextLabel = selected.label ?? selected.display_name;
      if (nextLabel) setLabel(nextLabel);
      setWeights({ ...defaultWeights(), ...selected.feature_group_weights });
      setRun(null);
    },
    [user, configsCatalog, setConfig, setLabel, setWeights, setRun],
  );

  const onSelectSavedRun = useCallback(
    async (runId: string) => {
      if (!user) return;
      if (!runId) {
        setRun(null);
        return;
      }
      setBusy("load_run");
      setError(null);
      try {
        const latestRun = await api.getModelRun(runId, { idToken: user.idToken });
        setRun(latestRun);
      } catch (err) {
        setError((err as Error).message);
      } finally {
        setBusy(null);
      }
    },
    [user, setRun],
  );

  const createConfig = useCallback(async () => {
    if (!user) return;
    setBusy("config");
    setError(null);
    try {
      const c = await api.createModelConfig(
        { label, feature_group_weights: weights },
        { idToken: user.idToken },
      );
      setConfig(c);
      setRun(null);
      const refreshed = await api.listModelConfigs({ idToken: user.idToken, limit: 50 });
      setConfigsCatalog(refreshed);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [user, label, weights, setConfig, setRun]);

  const submitRun = useCallback(async () => {
    if (!user || !config) return;
    if (!splitValid) {
      setError("Split is not valid for available labeled games.");
      return;
    }
    setBusy("run");
    setError(null);

    if (!config.config_id) {
      setError("Config is missing config_id.");
      setBusy(null);
      return;
    }

    try {
      const split =
        preset === "custom"
          ? {
              train_end_date: trainEnd,
              eval_start_date: evalStart,
              eval_end_date: evalEnd,
            }
          : undefined; // Let the backend default based on labeled games.
      const r = await api.createModelRun(
        {
          config_id: config.config_id,
          split,
        },
        { idToken: user.idToken },
      );
      setRun(r);
      const refreshedRuns = await api.listModelRuns({ idToken: user.idToken, limit: 50 });
      setRunsCatalog(refreshedRuns);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [user, config, splitValid, preset, trainEnd, evalStart, evalEnd, setRun]);

  const activate = useCallback(async () => {
    if (!user || !run) return;
    setBusy("activate");
    setError(null);
    setActivateMessage(null);
    try {
      const active = await api.setActiveModel({ run_id: run.run_id }, { idToken: user.idToken });
      setActiveModel(active);
      refreshActiveModel();
      setActivateMessage(
        active.run_id
          ? `Active model updated. Slate requests will use run ${active.run_id} (reload Predictions to see changes).`
          : "Active model cleared.",
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }, [user, run, refreshActiveModel]);

  if (!user) {
    return (
      <section className="page">
        <h1>Model Lab</h1>
        <p className="muted">
          Sign in to build a custom WNBA prediction model. You&apos;ll set
          feature-group weights, pick a time split, and evaluate your model
          against the baseline on held-out future games.
        </p>
      </section>
    );
  }


  return (
    <section className="page">
      <header className="page-header">
        <h1>Model Lab</h1>
        {activeModelSummary && (
          <span className="active-model-badge">
            Active model: {activeModelSummary.configLabel} · run {activeModelSummary.runShort}…
          </span>
        )}
        {!activeModelSummary && activeModelError && (
          <span className="muted small" title={activeModelError}>
            Active model: unavailable
          </span>
        )}
        <p className="muted">
          Weight the feature groups you trust more. Training and evaluation
          happen on strictly ordered time splits — no peeking at the eval
          window.
        </p>
      </header>

      <div className="card">
        <h2>Active model</h2>
        {activeModel?.run_id ? (
          <p className="muted small">
            Run <code>{activeModel.run_id}</code>
            {activeRunSummary?.cfgTitle ? (
              <>
                {" "}
                · config {activeRunSummary.cfgTitle}
              </>
            ) : null}
            {" "}
            · updated <code>{activeModel.updated_at}</code>
          </p>
        ) : (
          <p className="muted small">
            No personal model is active yet. The Predictions page will show league baseline only
            until you activate a successful run.
          </p>
        )}
      </div>

      <div className="card">
        <h2>1. Configure</h2>
        {configsCatalog.length > 0 && (
          <label className="field">
            <span>Saved config</span>
            <select
              value={config?.config_id ?? ""}
              onChange={(e) => void onSelectSavedConfig(e.target.value)}
              disabled={busy !== null}
            >
              {configsCatalog.map((c) => {
                const title = (c.label ?? c.display_name ?? "Untitled").trim() || "Untitled";
                return (
                  <option key={c.config_id} value={c.config_id}>
                    {title} · {c.config_id.slice(0, 8)}…
                  </option>
                );
              })}
            </select>
          </label>
        )}
        {configWeightsMissing && (
          <p className="muted small">
            This saved config has no stored feature weights (common for configs created before a
            server-side fix). Adjust sliders and click &quot;Save config&quot; to repair it, then
            train again.
          </p>
        )}
        <label className="field">
          <span>Label</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="my-first-model"
          />
        </label>

        <div className="weights-grid">
          {FEATURE_GROUPS.map((g) => (
            <label key={g.key} className="weight-row">
              <div>
                <strong>{g.label}</strong>
                <div className="muted small">{g.description}</div>
              </div>
              <div className="weight-control">
                <input
                  type="range"
                  min={0}
                  max={3}
                  step={0.1}
                  value={weights[g.key] ?? 1}
                  onChange={(e) => setWeight(g.key, Number(e.target.value))}
                />
                <span className="weight-value">
                  {(weights[g.key] ?? 1).toFixed(1)}×
                </span>
              </div>
            </label>
          ))}
        </div>

        <button
          type="button"
          className="btn btn-primary"
          disabled={busy !== null}
          onClick={createConfig}
        >
          {busy === "config" ? "Saving…" : "Save config"}
        </button>
        {config && (
          <div className="muted small" style={{ marginTop: "1rem" }}>
            <p>Config id: <code>{config.config_id}</code></p>
          </div>
        )}
      </div>

      <div className="card" aria-disabled={!config}>
        <h2>2. Train &amp; evaluate</h2>
        {runsCatalog.length > 0 && (
          <label className="field">
            <span>Saved run</span>
            <select
              value={run?.run_id ?? ""}
              onChange={(e) => void onSelectSavedRun(e.target.value)}
              disabled={busy !== null}
            >
              <option value="">None selected</option>
              {runsCatalog.map((r) => {
                const when = (r as { created_at?: string }).created_at?.slice(0, 19) ?? "unknown time";
                return (
                  <option key={r.run_id} value={r.run_id}>
                    {r.status} · {when} · {r.run_id.slice(0, 8)}…
                  </option>
                );
              })}
            </select>
          </label>
        )}
        <label className="field">
          <span>Evaluation preset</span>
          <select value={preset} onChange={(e) => setPreset(e.target.value as SplitPresetKey)}>
            <option value="default_30">Default (last 30 labeled days)</option>
            <option value="last_60">Last 60 labeled days</option>
            <option value="last_90">Last 90 labeled days</option>
            <option value="custom">Advanced: pick dates</option>
          </select>
        </label>

        {preset === "custom" && (
          <div className="split-grid">
            <label className="field">
              <span>Train end (inclusive)</span>
              <input type="date" value={trainEnd} onChange={(e) => setTrainEnd(e.target.value)} />
            </label>
            <label className="field">
              <span>Eval start</span>
              <input type="date" value={evalStart} onChange={(e) => setEvalStart(e.target.value)} />
            </label>
            <label className="field">
              <span>Eval end</span>
              <input type="date" value={evalEnd} onChange={(e) => setEvalEnd(e.target.value)} />
            </label>
          </div>
        )}

        <div className="muted small" style={{ marginTop: "0.75rem" }}>
          {splitPreviewBusy && <p>Checking split…</p>}
          {splitPreview && (
            <>
              <p>
                Labeled games available:{" "}
                <code>
                  {splitPreview.labeled_game_date_range.min ?? "?"} →{" "}
                  {splitPreview.labeled_game_date_range.max ?? "?"}
                </code>
              </p>
              <p>
                Train games: <strong>{splitPreview.counts.n_train}</strong> · Eval games:{" "}
                <strong>{splitPreview.counts.n_eval}</strong>
              </p>
              {splitPreview.warnings?.length > 0 && (
                <p className="muted small">{splitPreview.warnings.join(" ")}</p>
              )}
              {splitPreview.errors?.length > 0 && (
                <p className="error small">{splitPreview.errors.join(" ")}</p>
              )}
            </>
          )}
        </div>

        <button
          type="button"
          className="btn btn-primary"
          disabled={!config || !splitValid || busy !== null || run?.status === "pending" || run?.status === "running"}
          onClick={submitRun}
        >
          {(busy === "run" || run?.status === "pending" || run?.status === "running") && (
            <span className="btn-spinner" aria-hidden />
          )}
          {busy === "run"
            ? "Submitting…"
            : run?.status === "pending" || run?.status === "running"
              ? "Training…"
              : "Submit run"}
        </button>
        {run && (
          <div className="run-summary">
            <p>
              Run <code>{run.run_id}</code>: <strong>{run.status}</strong>
            </p>
            <p className="muted small">
              Log loss is a standard scoring rule for probabilities: it penalizes confident wrong
              predictions more than cautious ones. Lower is better. Brier score measures how close
              your predicted win probabilities are to the true 0/1 outcomes (calibration + sharpness
              for a binary event). Lower is better. N is the number of held-out games in the eval
              window.
            </p>
            {run.metrics &&
              run.metrics.baseline &&
              run.metrics.custom &&
              typeof run.metrics.baseline.log_loss === "number" &&
              typeof run.metrics.baseline.brier_score === "number" &&
              typeof run.metrics.baseline.n_samples === "number" &&
              typeof run.metrics.custom.log_loss === "number" &&
              typeof run.metrics.custom.brier_score === "number" &&
              typeof run.metrics.custom.n_samples === "number" && (
              <table className="metrics-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Log loss</th>
                    <th>Brier</th>
                    <th>N</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Baseline</td>
                    <td>{formatFixed4(run.metrics.baseline.log_loss)}</td>
                    <td>{formatFixed4(run.metrics.baseline.brier_score)}</td>
                    <td>{run.metrics.baseline.n_samples}</td>
                  </tr>
                  <tr>
                    <td>My model</td>
                    <td>{formatFixed4(run.metrics.custom.log_loss)}</td>
                    <td>{formatFixed4(run.metrics.custom.brier_score)}</td>
                    <td>{run.metrics.custom.n_samples}</td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      <div className="card" aria-disabled={!run || run.status !== "succeeded"}>
        <h2>3. Activate</h2>
        <p className="muted small">
          Setting this run as active makes its predictions show up next to the
          baseline on the slate. You can always switch back.
        </p>
        <button
          type="button"
          className="btn btn-primary"
          disabled={!run || run.status !== "succeeded" || busy !== null}
          onClick={activate}
        >
          {busy === "activate" ? "Activating…" : "Activate this run"}
        </button>
        {activateMessage && <p className="muted small">{activateMessage}</p>}
      </div>

      {error && <p className="error">{error}</p>}
    </section>
  );
}
